<template>
  <TransitionFade>
    <button
      v-bind="$attrs"
      v-if="modelValue"
      class="fixed flex flex-col items-center justify-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] bg-white dark:bg-neutral-800 rounded-xl p-2 md:p-4 space-y-2 md:space-y-4"
      style="direction: ltr"
      @click.stop="stop"
    >
      <h3 class="text-center font-semibold text-sm md:text-lg">
        {{
          t(
            `components.reader.Reader2HintCard.reading_direction_hint.${hintDirection}`,
          )
        }}
      </h3>
      <span class="flex justify-center items-center">
        <canvas class="max-h-40 min-w-0" ref="canvas" />
      </span>
      <NamiButton button-type="primary" text pill block @click.stop="stop">
        Dismiss
      </NamiButton>
    </button>
  </TransitionFade>
</template>

<script setup lang="ts">
import type { TitleReadingMode } from "~/src/api";
import { DotLottie } from "@lottiefiles/dotlottie-web";

interface Props {
  sourceReadingDirection: TitleReadingMode;
  readingDirection: "ltr" | "rtl";
  scrollDirection: "horizontal" | "vertical";
  isVls: boolean;
  modelValue: boolean;
}

const canvas = ref<HTMLCanvasElement>();
const lottiePlayer = ref<DotLottie>();

const theme = useCurrentTheme();

const props = withDefaults(defineProps<Props>(), {
  sourceReadingDirection: "ltr",
  scrollDirection: "horizontal",
  readingDirection: "ltr",
});

const emit = defineEmits<{
  (e: "update:modelValue", v: Props["modelValue"]): void;
}>();

const nuxtApp = useNuxtApp();
const { locale, t } = useI18n();

const map: {
  [k in `${Props["sourceReadingDirection"]}.${Props["readingDirection"]}`]: string;
} = {
  "ltr.ltr": "horizontal_ltr.json",
  "ltr.rtl": "horizontal_ltr_reverse.json",
  "rtl.ltr": "horizontal_rtl_reverse.json",
  "rtl.rtl": "horizontal_rtl.json",
  "vls.ltr": "vertical.json",
  "vls.rtl": "vertical.json",
};

const hintDirection = computed(() => {
  if (props.isVls) {
    return "vls";
  }
  return props.sourceReadingDirection;
});

function getDynamicUrl(): string {
  let file = map[`${hintDirection.value}.${props.readingDirection}`];
  if (
    props.scrollDirection === "vertical" &&
    props.sourceReadingDirection !== "vls"
  ) {
    file = `vertical_${props.sourceReadingDirection}_numbered.json`;
  }

  return getAbsoluteAssetLink(
    `animations/readingDirection/${
      theme.value === "dark" ? "dark_" : ""
    }${file}`,
  );
}

const stop = () => emit("update:modelValue", false);
watch(
  () => props.modelValue,
  (v) => {
    if (v) {
      nextTick(() => createLottie());
    } else {
      destroyLottie();
    }
  },
  { immediate: true },
);

function createLottie() {
  if (!canvas.value) return;
  if (lottiePlayer.value) destroyLottie();

  lottiePlayer.value = new DotLottie({
    autoplay: true,
    loop: true,
    canvas: canvas.value,
    src: getDynamicUrl(),
    layout: {
      fit: "contain",
      align: [0.5, 0.5],
    },
  });

  let plays = 0;

  lottiePlayer.value.addEventListener("loop", () => {
    if (plays++ > 0) {
      stop();
    }
  });
}

function destroyLottie() {
  if (!lottiePlayer.value) return;
  lottiePlayer.value.destroy();
}
</script>

<style scoped lang="postcss"></style>
